import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import { useAuthStore } from '@/stores/useAuthStore.js';
import { useConnectionProfileStore } from '@/stores/useConnectionProfileStore.js';
import { useConnectionManager } from '@/hooks/useConnectionManager.js';
import { useIntegrationStore } from '@/stores/useIntegrationStore.js';
import { AdapterType, type ConnectionProfile, type IntegrationDirection, type IntegrationStats } from '@k-ucap/shared';

const adapterTypeLabels: Record<AdapterType, string> = {
  VIRTUAL: 'Virtual',
  REPLAY: 'Replay',
  WEBSOCKET: 'WebSocket',
  LOCAL_BRIDGE: 'Local Bridge',
  UDP: 'UDP',
  UDP_MULTICAST: 'UDP Multicast',
  TCP_CLIENT: 'TCP Client',
  TCP_SERVER: 'TCP Server',
};

const directionLabels: Record<IntegrationDirection, string> = {
  INBOUND: '수신 (INBOUND)',
  OUTBOUND: '송신 (OUTBOUND)',
  BIDIRECTIONAL: '양방향 (BIDIRECTIONAL)',
};

export const IntegrationPage = () => {
  const selectedProject = useAuthStore((s) => s.selectedProject);
  const userProfile = useAuthStore((s) => s.userProfile);
  const environment = selectedProject?.environment ?? 'development';

  const profiles = useConnectionProfileStore((s) => s.profiles);
  const selectedProfileId = useConnectionProfileStore((s) => s.selectedProfileId);
  const isLoading = useConnectionProfileStore((s) => s.isLoading);
  const error = useConnectionProfileStore((s) => s.error);
  const loadProfiles = useConnectionProfileStore((s) => s.loadProfiles);
  const selectProfile = useConnectionProfileStore((s) => s.selectProfile);
  const createProfile = useConnectionProfileStore((s) => s.createProfile);
  const updateProfile = useConnectionProfileStore((s) => s.updateProfile);
  const duplicateProfile = useConnectionProfileStore((s) => s.duplicateProfile);
  const deleteProfile = useConnectionProfileStore((s) => s.deleteProfile);
  const clearError = useConnectionProfileStore((s) => s.clearError);

  const adapters = useIntegrationStore((s) => s.adapters);
  const totalStats = useMemo(() => {
    const empty: IntegrationStats = { rxCount: 0, txCount: 0, rxBytes: 0, txBytes: 0, errors: 0, reconnects: 0 };
    return Object.values(adapters).reduce(
      (total, adapter) => ({
        rxCount: total.rxCount + adapter.stats.rxCount,
        txCount: total.txCount + adapter.stats.txCount,
        rxBytes: total.rxBytes + adapter.stats.rxBytes,
        txBytes: total.txBytes + adapter.stats.txBytes,
        errors: total.errors + adapter.stats.errors,
        reconnects: total.reconnects + adapter.stats.reconnects,
      }),
      empty
    );
  }, [adapters]);
  const allAdapters = useMemo(() => Object.values(adapters), [adapters]);

  const { connect, disconnect, sendTestMessage } = useConnectionManager();
  const packets = useIntegrationStore((s) => s.packets);
  const clearPackets = useIntegrationStore((s) => s.clearPackets);
  // const totalStats = useIntegrationStore((s) => s.getTotalStats());
  // const adapters = useIntegrationStore((s) => s.getAllAdapters());

  const [connectedIds, setConnectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (selectedProject) {
      loadProfiles(selectedProject.id);
    }
  }, [selectedProject, loadProfiles]);

  const selectedProfile = useMemo(
    () => profiles.find((p) => p.id === selectedProfileId) ?? null,
    [profiles, selectedProfileId]
  );

  const handleCreate = async () => {
    if (!selectedProject || !userProfile) return;
    await createProfile(selectedProject.id, userProfile.uid, environment);
  };

  const handleConnect = (profile: ConnectionProfile) => {
    connect(profile);
    setConnectedIds((prev) => new Set([...prev, profile.id]));
  };

  const handleDisconnect = (profileId: string) => {
    disconnect(profileId);
    setConnectedIds((prev) => {
      const next = new Set(prev);
      next.delete(profileId);
      return next;
    });
  };

  const handleUpdate = (patch: Partial<ConnectionProfile>) => {
    if (!selectedProfile) return;
    updateProfile({ ...selectedProfile, ...patch });
  };

  const handleConfigUpdate = (patch: Record<string, unknown>) => {
    if (!selectedProfile) return;
    updateProfile({
      ...selectedProfile,
      configuration: { ...selectedProfile.configuration, ...patch },
    });
  };

  const handleReconnectUpdate = (patch: Partial<ConnectionProfile['reconnectPolicy']>) => {
    if (!selectedProfile) return;
    updateProfile({
      ...selectedProfile,
      reconnectPolicy: { ...selectedProfile.reconnectPolicy, ...patch },
    });
  };

  const isConnected = (profileId: string) => connectedIds.has(profileId);

  const selectedAdapter = allAdapters.find((a) => a.id === selectedProfile?.id);
  const getProfileStatus = (profileId: string) => adapters[profileId]?.status ?? 'disconnected';

  const statusColor = (status: string): 'success' | 'info' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'connected':
        return 'success';
      case 'connecting':
      case 'reconnecting':
        return 'info';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const statusLabel = (status: string): string => {
    switch (status) {
      case 'connected':
        return 'Connected';
      case 'connecting':
        return 'Connecting';
      case 'reconnecting':
        return 'Reconnecting';
      case 'error':
        return 'Error';
      default:
        return 'Disconnected';
    }
  };

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h4" gutterBottom>
        연동 관리
      </Typography>

      {!selectedProject && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          프로젝트를 선택해야 연동 프로파일을 관리할 수 있습니다.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ flexGrow: 1, minHeight: 0 }}>
        <Grid size={{ xs: 12, md: 3 }} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Paper sx={{ flexGrow: 1, overflow: 'auto', p: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mb: 1 }}>
              <Button size="small" startIcon={<AddIcon />} onClick={handleCreate} disabled={!selectedProject}>
                새 연동
              </Button>
            </Box>
            <List dense>
              {isLoading ? (
                <ListItem>
                  <ListItemText primary="로딩 중..." />
                </ListItem>
              ) : (
                profiles.map((p) => (
                  <ListItem
                    key={p.id}
                    disablePadding
                    secondaryAction={
                      <Box>
                        <IconButton size="small" onClick={() => duplicateProfile(p.id)}>
                          <FileCopyIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => deleteProfile(p.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemButton selected={selectedProfileId === p.id} onClick={() => selectProfile(p.id)}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {p.name}
                            <Chip
                              label={statusLabel(getProfileStatus(p.id))}
                              size="small"
                              color={statusColor(getProfileStatus(p.id))}
                            />
                          </Box>
                        }
                        secondary={adapterTypeLabels[p.adapterType]}
                      />
                    </ListItemButton>
                  </ListItem>
                ))
              )}
            </List>
          </Paper>
        </Grid>

        <Grid
          size={{ xs: 12, md: 5 }}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%', overflow: 'auto' }}
        >
          {selectedProfile ? (
            <>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {selectedProfile.name}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        label="이름"
                        fullWidth
                        size="small"
                        value={selectedProfile.name}
                        onChange={(e) => handleUpdate({ name: e.target.value })}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        label="설명"
                        fullWidth
                        size="small"
                        value={selectedProfile.description}
                        onChange={(e) => handleUpdate({ description: e.target.value })}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <FormControl fullWidth size="small" data-testid="integration-adapter-select">
                        <InputLabel>Adapter</InputLabel>
                        <Select
                          value={selectedProfile.adapterType}
                          label="Adapter"
                          onChange={(e) => handleUpdate({ adapterType: e.target.value as AdapterType })}
                        >
                          {Object.entries(adapterTypeLabels).map(([value, label]) => (
                            <MenuItem key={value} value={value}>
                              {label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Direction</InputLabel>
                        <Select
                          value={selectedProfile.direction}
                          label="Direction"
                          onChange={(e) => handleUpdate({ direction: e.target.value as IntegrationDirection })}
                        >
                          {Object.entries(directionLabels).map(([value, label]) => (
                            <MenuItem key={value} value={value}>
                              {label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={selectedProfile.enabled}
                            onChange={(e) => handleUpdate({ enabled: e.target.checked })}
                          />
                        }
                        label="Enabled"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={selectedProfile.autoConnect}
                            onChange={(e) => handleUpdate({ autoConnect: e.target.checked })}
                          />
                        }
                        label="Auto Connect"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    설정
                  </Typography>
                  {selectedProfile.adapterType === AdapterType.WEBSOCKET && (
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          label="WebSocket URL"
                          fullWidth
                          size="small"
                          value={String(selectedProfile.configuration.url ?? '')}
                          onChange={(e) => handleConfigUpdate({ url: e.target.value })}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          label="Token"
                          fullWidth
                          size="small"
                          type="password"
                          value={String(selectedProfile.configuration.token ?? '')}
                          onChange={(e) => handleConfigUpdate({ token: e.target.value })}
                        />
                      </Grid>
                    </Grid>
                  )}
                  {selectedProfile.adapterType !== AdapterType.WEBSOCKET && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      이 Adapter는 WebSocket 외 연동을 위한 구조입니다. Prompt 09에서는 WebSocket을 기본으로 지원합니다.
                    </Alert>
                  )}
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    재연결 정책
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={selectedProfile.reconnectPolicy.enabled}
                            onChange={(e) => handleReconnectUpdate({ enabled: e.target.checked })}
                          />
                        }
                        label="Enabled"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        label="Interval (ms)"
                        type="number"
                        fullWidth
                        size="small"
                        value={selectedProfile.reconnectPolicy.intervalMs}
                        onChange={(e) => handleReconnectUpdate({ intervalMs: Number(e.target.value) })}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        label="Max Retries"
                        type="number"
                        fullWidth
                        size="small"
                        value={selectedProfile.reconnectPolicy.maxRetries}
                        onChange={(e) => handleReconnectUpdate({ maxRetries: Number(e.target.value) })}
                      />
                    </Grid>
                  </Grid>
                  <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                    {selectedAdapter?.status !== 'connected' ? (
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<PlayArrowIcon />}
                        onClick={() => handleConnect(selectedProfile)}
                        disabled={selectedProfile.adapterType !== AdapterType.WEBSOCKET}
                      >
                        연결
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<StopIcon />}
                        onClick={() => handleDisconnect(selectedProfile.id)}
                      >
                        연결 해제
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<SendIcon />}
                      onClick={() => selectedProfile && sendTestMessage(selectedProfile)}
                      disabled={!isConnected(selectedProfile.id)}
                    >
                      Test Send
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </>
          ) : (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">왼쪽에서 연동 프로파일을 선택하거나 새 연동을 생성하세요.</Typography>
            </Paper>
          )}
        </Grid>

        <Grid
          size={{ xs: 12, md: 4 }}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%', overflow: 'auto' }}
        >
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                연결 상태
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Chip label={`RX: ${totalStats.rxCount}`} />
                <Chip label={`TX: ${totalStats.txCount}`} />
                <Chip label={`Errors: ${totalStats.errors}`} />
                <Chip label={`Reconnects: ${totalStats.reconnects}`} />
              </Box>
              {selectedProfile && selectedAdapter && (
                <Box>
                  <Typography variant="body2">Status: {selectedAdapter.status}</Typography>
                  <Typography variant="body2">
                    RX: {selectedAdapter.stats.rxCount} / TX: {selectedAdapter.stats.txCount}
                  </Typography>
                  {selectedAdapter.lastError && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {selectedAdapter.lastError}
                    </Alert>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>

          <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" gutterBottom>
                  패킷 모니터
                </Typography>
                <Button size="small" startIcon={<RefreshIcon />} onClick={clearPackets}>
                  Clear
                </Button>
              </Box>
              <TableContainer component={Paper} variant="outlined" sx={{ flexGrow: 1, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>Dir</TableCell>
                      <TableCell>Adapter</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Size</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {packets.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography color="text.secondary">패킷이 없습니다.</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {packets.slice(0, 100).map((p) => (
                      <TableRow key={p.id}>
                        <TableCell>{new Date(p.timestamp).toLocaleTimeString()}</TableCell>
                        <TableCell>{p.direction}</TableCell>
                        <TableCell>
                          <Tooltip title={p.adapterName}>
                            <span>{p.adapterType}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{p.messageType ?? '-'}</TableCell>
                        <TableCell>{p.sizeBytes}</TableCell>
                        <TableCell>
                          <Chip
                            label={p.status}
                            size="small"
                            color={p.status === 'ok' ? 'success' : p.status === 'warn' ? 'warning' : 'error'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default IntegrationPage;
